import React, { useRef, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollText, Upload, Trash2, Loader2, FileText } from "lucide-react";
import { toast } from "sonner";

// Rules & Regulations library that grounds every Oracle answer (Galleon admins manage)
export default function DocsPanel({ data, onChanged }) {
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef();

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData(); fd.append("file", file); fd.append("title", title);
    try {
      const { data: d } = await api.post("/oracle/docs", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`"${d.doc.title}" indexed — ${d.doc.sections} sections`);
      setTitle(""); onChanged();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  const remove = async (d) => {
    if (!window.confirm(`Remove "${d.title}" from the Oracle?`)) return;
    try { await api.delete(`/oracle/docs/${d.id}`); toast.success("Document removed"); onChanged(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <Card className="gold-ring glass h-fit p-5" data-testid="oracle-docs-panel">
      <div className="flex items-center gap-2"><ScrollText className="h-5 w-5 text-[hsl(var(--gold))]" /><h3 className="font-display text-lg font-700">Rules & Regulations</h3></div>
      <p className="mt-1 text-xs text-muted-foreground">The Oracle answers only from these governing documents.</p>
      {data.can_manage && (
        <div className="mt-4 rounded-xl border border-[hsl(var(--gold))]/25 bg-background/40 p-3" data-testid="oracle-upload-box">
          <Label className="text-xs">Document title (optional)</Label>
          <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. IOTA Galleon Constitution 2026" className="mt-1" data-testid="oracle-doc-title-input" />
          <input ref={fileRef} type="file" accept=".pdf,.docx,.txt,.md" hidden onChange={(e) => upload(e.target.files?.[0])} data-testid="oracle-doc-file-input" />
          <Button onClick={() => fileRef.current?.click()} disabled={busy} className="mt-2 w-full bg-[hsl(var(--gold))] font-700 text-[hsl(var(--primary-foreground))]" data-testid="oracle-upload-button">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Upload className="mr-2 h-4 w-4" />Upload PDF / DOCX / TXT</>}
          </Button>
        </div>
      )}
      <ul className="mt-4 space-y-2" data-testid="oracle-docs-list">
        {data.docs.length === 0 && <li className="rounded-lg border border-dashed border-border p-4 text-center text-xs text-muted-foreground" data-testid="oracle-docs-empty">No documents yet{data.can_manage ? " — upload the Rules & Regulations above." : ". Ask a Galleon admin to upload them."}</li>}
        {data.docs.map((d) => (
          <li key={d.id} className="flex items-start gap-3 rounded-lg bg-secondary/50 p-3" data-testid={`oracle-doc-${d.id}`}>
            <FileText className="mt-0.5 h-4 w-4 shrink-0 text-[hsl(var(--water))]" />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-600">{d.title}</p>
              <p className="text-[0.65rem] text-muted-foreground">{d.sections} sections · {d.uploaded_by} · {new Date(d.created_at).toLocaleDateString()}</p>
            </div>
            {data.can_manage && <button onClick={() => remove(d)} className="text-muted-foreground hover:text-destructive" data-testid={`oracle-doc-delete-${d.id}`}><Trash2 className="h-4 w-4" /></button>}
          </li>
        ))}
      </ul>
    </Card>
  );
}
